import * as React from 'react'
import { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { RootState, StoreState } from '../../state/configureStore'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import Page from '../../components/molecules/page'
import { Info } from '@mui/icons-material'
import { MachineSoftwareVersionHistory } from '../customers/machineGraph/MachineSoftwareVeriosn'
import { MachineSoftwareVersionTable } from '../customers/machineGraph/MachineSoftwareVersionTable'
import GppGoodIcon from '@mui/icons-material/GppGood';
import { text_colors } from '../../theme'
import { fetchMachinesList } from '../../state/actions/machine'
import { Box, Tooltip } from '@mui/material'
import { LabledCheckbox } from '../../components/molecules/LabledCheckbox'
import { ColumnDefinition, TableView } from '../../components/atoms/table'
import { itemFilter } from '../../utils/searchHelpers'
import { ConnectionStateTag } from '../../components/molecules/connectionStateTag'
import { MachinesListItem } from '../../state/reducers/machine'
import { Tag } from '../../components/molecules/tag'
import { TagDefenition } from './types'
import { useQueryBackend } from '../../utils/requestHelpers'
import { DataLoadingText } from '../../components/atoms/text/dataLoadingText'
import { MultipleSelectInput, SelectOption } from '../../components/atoms/inputs/multipleSelectInput'
import { FetchFromBackoffice } from '../../utils/request'
import { TagList } from '../settings/types'
import { SearchInput } from '../../components/atoms/inputs/searchInput'
import { HubHealthTable } from './hubHealthTable'
import { isProd } from '../../config'

interface FetchResponseItem {
  id: string
  serialNumber: string
  nodeId: string | undefined
  nodeName: string | undefined
  parents: [
    {
      id: string
      name: string
    }
  ],
  tags: TagDefenition[]
  monitored: boolean
  connectionStatus: 'Unknown' | 'Connected' | 'Disconnected'
  totalCount: string
}

const tagSearch = (machine: MachinesListItem, query: string | null): boolean => {
  if (!query)
    return true

  return machine.tags.some(t => t.tag.toString().toLowerCase().includes(query.toLowerCase()))
}

const tagFilter = (machine: MachinesListItem, selectedTags: string[]): boolean => {
  if (!selectedTags || selectedTags.length === 0) return true
  return machine.tags.some(t => selectedTags.includes(t.tag));
}

function Machines(
  state: ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>
): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const showAll = searchParams.has('showAll')
  const query = searchParams.get('q')
  const [sortedMachines, setSortedMachines] = useState<MachinesListItem[]>([])
  const [fetchedMachines, setFetchedMachines] = useState<MachinesListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const queryApi = useQueryBackend('Backoffice')
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  useEffect(() => {
    state.fetchMachines()
  }, [])

  useEffect(() => {
    setFetchedMachines(state.machines)
    setIsLoading(false)
  }, [JSON.stringify(state.machines)])

  useEffect(() => {
    if (fetchedMachines.length == 0) return
    const filtered = fetchedMachines
      .filter(m => showAll ? true : m.monitored)
      .filter(m => tagFilter(m, selectedTags) && (tagSearch(m, query) || itemFilter(m, query, ["serialId", "owner", "nodeName", "connection"])))
    setSortedMachines(filtered)


  }, [fetchedMachines, showAll, query, selectedTags])


  useEffect(() => {
    const tagsFromUrl = searchParams.getAll('t')
    setSelectedTags(tagsFromUrl)
  }, [searchParams.toString()])

  const handleTagsChange = (tags: string[]) => {
    searchParams.delete('t')

    tags.forEach(t => searchParams.append('t', t))

    navigate('?' + searchParams.toString())
  }

  const MachineTableSection = ({ machines, isLoading }: { machines: MachinesListItem[], isLoading: boolean }) => {

    const isMobile = useSelector<RootState>(state => state.dimensions.device) === "mobile"

    return (
      <Box>
        {isLoading ? (
          <DataLoadingText showSpinner text={'Loading hubs...'} />
        ) : (
          <TableView
            isClickable
            items={machines}
            paging
            rowsPerPage={100}
            columns={[
              {
                Property: "monitored",
                Title: '',
                Render: (item) => item.monitored && !isMobile ? <GppGoodIcon sx={{ color: text_colors.greyBlue, marginBottom: '-5px' }} /> : null,
              },
              !isMobile && {
                Property: "serialId",
                Title: 'Serial Id',

              },
              {
                Property: "nodeName",
                Title: "Store",
                Render: (item) => item.nodeName || "-"
              },
              !isMobile && {
                Property: "owner",
                Title: "Owner",
              },
              {
                Property: "totalCount",
                Title: 'Run Cycles',
                sortFn: (a: MachinesListItem, b: MachinesListItem) => Number(a.totalCount) - Number(b.totalCount)
              },
              {
                Property: "tags",
                Title: "Tags",
                Sortable: false,
                Render: (item) =>
                  <div style={{ display: "flex", flexDirection: "row", gap: 4, maxWidth: !isMobile ? "320px" : "120px", flexWrap: "wrap" }}>

                    {item.tags.map(tag => (
                      <Tooltip title={tag.tag}>
                        <div>
                          <Tag text={tag.tag} colorCode={tag.colorCode} backgroundColor />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
              },
              {
                Property: "connection",
                Title: "Connected",
                Render: (item) => <ConnectionStateTag connectionState={item.connection} />
              }
            ].filter(Boolean) as ColumnDefinition<MachinesListItem>[]}
            onRowClick={item => navigate(item.serialId)}
            defaultSortColumn='nodeName'
          />)}
      </Box>
    )
  }

  const handleCheckShowAll = (checked: boolean) => {
    checked ? searchParams.set('showAll', 'true') : searchParams.delete('showAll')
    navigate('?' + searchParams.toString())
  }

  const handleSearch = (query: string) => {
    if (!query || query.length === 0)
      searchParams.delete('q')
    else
      searchParams.set('q', query)

    navigate('?' + searchParams.toString())
  }

  const tabs = [
    {
      name: "Hubs",
      icon: <Info />,
      pageSections: [
        <TopSection
          showAll={showAll}
          onShowAll={handleCheckShowAll}
          query={query}
          onSearch={handleSearch}
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
          setSelectedTags={setSelectedTags} />,
        <MachineTableSection machines={sortedMachines} isLoading={isLoading} />
      ],
    },
    {
      name: "Version History",
      icon: <Info />,
      pageSections: [
        <LabledCheckbox
          isChecked={showAll}
          onChange={handleCheckShowAll}
          label={"All hubs"}
        />,
        <MachineSoftwareVersionHistory seeAllMachines={showAll} />,
        <MachineSoftwareVersionTable seeAllMachines={showAll} />,
      ],
    },
    {
      name: "Health",
      icon: <Info />,
      pageSections: [
        <HubHealthTable showAll={false} key="health-table" />],
    },
  ]


  return (
    <Page
      title="Hubs"
      // topComponents={[]}
      sideScroll
      tabs={isProd() ? tabs.slice(0, 2) : tabs}
    >
    </Page>
  );
}

const TopSection = ({ showAll, query, onShowAll, onSearch, selectedTags, setSelectedTags, onTagsChange }: { showAll: boolean, onShowAll: (state: boolean) => void, query: string | null, onSearch: (query: string) => void, selectedTags: string[], setSelectedTags: (tags: string[]) => void, onTagsChange: (tags: string[]) => void }) => {
  const navigate = useNavigate()
  const [tagList, setTagList] = useState<TagList[]>([])

  useEffect(() => {
    const fetchTagsList = async () => {
      try {
        const allTags = await FetchFromBackoffice<TagList[]>('dashboard/tags')
        setTagList(allTags)
      } catch (error) {
        console.error('Failed to fetch tags:', error)
      }
    }
    fetchTagsList()
  }, [])

  const createSelectOptionList = (list: TagList[]): SelectOption[] => {
    return (list.map(item => ({
      item: item.text,
      icon: item.colorCode && (
        <Box style={{ width: "14px", marginRight: '8px' }}>
          <div
            style={{
              width: "14px",
              height: "14px",
              backgroundColor: item.colorCode,
              borderRadius: "50%",

            }}
          />
        </Box>
      )
    })))
  }


  return (
    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "26px" }}>
        <LabledCheckbox
          isChecked={showAll}
          onChange={onShowAll}
          label={"All hubs"}
        />
        <MultipleSelectInput title='Tags' options={createSelectOptionList(tagList)} values={selectedTags} onClose={(tags) => onTagsChange(tags)} />
      </div>
      <SearchInput value={query ?? ''} onSearch={onSearch} />
    </div>

  )
}

const mapStateToProps = (state: StoreState) => ({
  machines: state.machines_list,
  fetchingState: state.machine_fetching,
  dimensions: state.dimensions
})

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  fetchMachines: () => dispatch(fetchMachinesList())
})

export default connect(mapStateToProps, mapDispatchToProps)(Machines)